import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "mx-auto py-8" }
const _hoisted_2 = { class: "mb-8" }
const _hoisted_3 = { class: "text-3xl font-semibold text-gray-900" }
const _hoisted_4 = {
  key: 0,
  class: "flex justify-center items-center min-h-[400px]"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-gray-600" }, "Loading student data...", -1)
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = {
  key: 1,
  class: "flex justify-center items-center min-h-[400px]"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-gray-600" }, "No Lingo data available for this student yet.", -1)
const _hoisted_9 = [
  _hoisted_8
]
const _hoisted_10 = {
  key: 2,
  class: "space-y-6"
}
const _hoisted_11 = { class: "bg-white rounded-[1.25rem] shadow-sm p-3 border border-gray-200 grid grid-cols-1 lg:grid-cols-2 gap-3" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", null, "Spelling List", -1)
const _hoisted_13 = { style: {"display":"flex","align-items":"center","margin-top":"-2.25rem","justify-content":"center"} }
const _hoisted_14 = /*#__PURE__*/_createElementVNode("span", null, "Spelling Data", -1)
const _hoisted_15 = { class: "bg-white rounded-[1.25rem] shadow-sm p-3 border border-gray-200 grid grid-cols-1 lg:grid-cols-2 gap-3" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("span", null, "Vocabulary List", -1)
const _hoisted_17 = { style: {"display":"flex","align-items":"center","margin-top":"-2.25rem","justify-content":"center"} }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("span", null, "Vocabulary Data", -1)
const _hoisted_19 = {
  key: 0,
  class: "bg-white rounded-[1.25rem] shadow-sm p-3 border border-gray-200 grid grid-cols-1 lg:grid-cols-2 gap-3"
}
const _hoisted_20 = /*#__PURE__*/_createElementVNode("span", null, "Grammar List", -1)
const _hoisted_21 = { style: {"display":"flex","align-items":"center","margin-top":"-2.25rem","justify-content":"center"} }
const _hoisted_22 = /*#__PURE__*/_createElementVNode("span", null, "Grammar Data", -1)
const _hoisted_23 = {
  key: 1,
  class: "bg-white rounded-[1.25rem] shadow-sm p-3 border border-gray-200"
}
const _hoisted_24 = /*#__PURE__*/_createElementVNode("div", { class: "bg-gray-50 rounded-2xl p-16 text-center" }, [
  /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-gray-600 font-medium" }, "Grammar coming soon ✨")
], -1)
const _hoisted_25 = {
  key: 2,
  class: "text-sm text-gray-500 px-2 mt-4 text-left font-sans"
}

import { useLearnerStore } from '@/stores/learnerStore';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useLingoStore } from '@/stores/lingoStore';
import MasteryCircle from '@/components/lingo/MasteryCircle.vue';
import DataItem from '@/components/lingo/DataItem.vue';
import LingoDataItem from '../../components/manage/LingoDataItemComponent.vue';
import LingoProgressCircle from '../../components/manage/LingoProgressCircleComponent.vue';
import LingoCard from '../../components/manage/LingoCardComponent.vue';
import LingoButton from '../../components/manage/LingoButtonComponent.vue';
import { useSchoolStore } from '@/stores/schoolStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'LingoStudentDataView',
  setup(__props) {

const schoolStore = useSchoolStore();
// The school arrives after setup runs, so keep this as a computed value.
const grammarEnabled = computed(() => schoolStore.grammarEnabled);
// import DetailModal from '@/components/lingo/DetailModal.vue';

const route = useRoute();
const lingoStore = useLingoStore();
const learnerStore = useLearnerStore();

const showSpellingDetails = ref(false);
const showVocabularyDetails = ref(false);

const pages = computed(() => [
  { name: 'Lingo Data', path: '/main/lingo-data' },
  { name: `Data for ${displayName.value}` }
]);

const studentId = computed(() => route.params.studentId as string);
const selectedStudent = computed(() => learnerStore.getLearnerById(studentId.value) || null);

function capitalise(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const displayName = computed(() => {
  const student = selectedStudent.value;
  if (!student) return 'Unknown Student';
  return `${capitalise(student.firstName)} ${capitalise(student.lastName)}`.trim();
});

const studentLingoData = computed(() =>
  lingoStore.getLingoDataByStudentId(studentId.value)
);

const spellingPercentage = computed(() => {
  const mastered = studentLingoData.value?.currentSpellingMasteredCount || 0;
  const total = studentLingoData.value?.currentSpellingTotalCount || 0;
  if (total === 0) return 0;
  return Math.round((mastered / total) * 100);
});

const vocabularyPercentage = computed(() => {
  const mastered = studentLingoData.value?.currentVocabularyMasteredCount || 0;
  const total = studentLingoData.value?.currentVocabularyTotalCount || 0;
  if (total === 0) return 0;
  return Math.round((mastered / total) * 100);
});

const grammarPercentage = computed(() => {
  const mastered = studentLingoData.value?.grammarMasteredCount || 0;
  const total = studentLingoData.value?.grammarTotalCount || 0;
  if (total === 0) return 0;
  return Math.round((mastered / total) * 100);
});

const spellingDataItems = computed(() => {
  const metrics = studentLingoData.value?.performanceMetrics?.spelling;
  const isDiagnosticComplete = studentLingoData.value?.spellingDiagnosticComplete !== false;
  const levelText = `Level ${spellingLevel.value >= 7 ? '7+' : spellingLevel.value}`;
  return [
    {
      label: 'Gaps Filled',
      value: metrics ? metrics.totalGapsFilled.toString() : '0',
      color: 'purple'
    },
    {
      label: 'Currently at',
      value: isDiagnosticComplete ? levelText : `${levelText}*`,
      color: 'blue'
    }
  ];
});

const vocabularyDataItems = computed(() => {
  const metrics = studentLingoData.value?.performanceMetrics?.vocabulary;
  const isDiagnosticComplete = studentLingoData.value?.vocabularyDiagnosticComplete !== false;
  const levelText = `Level ${vocabularyLevel.value >= 7 ? '7+' : vocabularyLevel.value}`;
  return [
    {
      label: 'Gaps Filled',
      value: metrics ? metrics.totalGapsFilled.toString() : '0',
      color: 'purple'
    },
    {
      label: 'Currently at',
      value: isDiagnosticComplete ? levelText : `${levelText}*`,
      color: 'blue'
    }
  ];
});

const grammarDataItems = computed(() => {
  const metrics = studentLingoData.value?.performanceMetrics?.grammar;
  const isDiagnosticComplete = studentLingoData.value?.grammarDiagnosticComplete !== false;
  const levelText = `Level ${grammarLevel.value >= 7 ? '7+' : grammarLevel.value}`;
  return [
    {
      label: 'Gaps Filled',
      value: metrics ? metrics.totalGapsFilled.toString() : '0',
      color: 'purple'
    },
    {
      label: 'Currently at',
      value: isDiagnosticComplete ? levelText : `${levelText}*`,
      color: 'blue'
    }
  ];
});

const hasIncompleteDiagnostic = computed(() => {
  if (!studentLingoData.value) return false;
  return studentLingoData.value.spellingDiagnosticComplete === false || 
         studentLingoData.value.vocabularyDiagnosticComplete === false;
});

const spellingMasteryDisplay = computed(() => {
  const mastered = studentLingoData.value?.currentSpellingMasteredCount || 0;
  const total = studentLingoData.value?.currentSpellingTotalCount || 0;
  return `${mastered}/${total}`;
});

const vocabularyMasteryDisplay = computed(() => {
  const mastered = studentLingoData.value?.currentVocabularyMasteredCount || 0;
  const total = studentLingoData.value?.currentVocabularyTotalCount || 0;
  return `${mastered}/${total}`;
});

const grammarMasteryDisplay = computed(() => {
  const mastered = studentLingoData.value?.grammarMasteredCount || 0;
  const total = studentLingoData.value?.grammarTotalCount || 0;
  return `${mastered}/${total}`;
});

const spellingKCs = computed(() =>
  studentLingoData.value?.allKCs.filter(kc => kc.type === 'spelling') || []
);

const vocabularyKCs = computed(() =>
  studentLingoData.value?.allKCs.filter(kc => kc.type === 'vocabulary') || []
);

const spellingLevel = computed(() => {
  if (!studentLingoData.value?.performanceMetrics?.spelling) return 1;
  return studentLingoData.value.performanceMetrics.spelling.currentLevel;
});

const vocabularyLevel = computed(() => {
  if (!studentLingoData.value?.performanceMetrics?.vocabulary) return 1;
  return studentLingoData.value.performanceMetrics.vocabulary.currentLevel;
});

const grammarLevel = computed(() => {
  if (!studentLingoData.value?.performanceMetrics?.grammar) return 1;
  return studentLingoData.value.performanceMetrics.grammar.currentLevel;
});

onMounted(async () => {
  if (!studentLingoData.value) {
    await lingoStore.fetchStudentLingoData({
      studentIds: [studentId.value]
    });
  }
});

return (_ctx: any,_cache: any) => {
  const _component_page_title = _resolveComponent("page-title")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_page_title, {
      pages: pages.value,
      hideFilters: true
    }, null, 8, ["pages"]),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, _toDisplayString(displayName.value), 1)
      ]),
      (_unref(lingoStore).loader.fetchStudentLingoData)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _hoisted_6))
        : (!studentLingoData.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, _hoisted_9))
          : (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(LingoCard, {
                  shadow: "none",
                  background: "gray",
                  rounded: "xl"
                }, {
                  title: _withCtx(() => [
                    _hoisted_12
                  ]),
                  action: _withCtx(() => [
                    _createVNode(_component_router_link, {
                      to: `/main/lingo-spelling-list/student/${studentId.value}`
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LingoButton, {
                          size: "sm",
                          hierarchy: "link-color",
                          "data-testid": "btn-primary-sm"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Details ")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_13, [
                      _createVNode(LingoProgressCircle, {
                        size: "sm",
                        shape: "half",
                        color: "#fbbf24",
                        caption: "Mastered",
                        "show-percent-sign": false,
                        value: spellingPercentage.value,
                        "display-value": spellingMasteryDisplay.value,
                        mastered: studentLingoData.value.currentSpellingMasteredCount,
                        total: studentLingoData.value.currentSpellingTotalCount
                      }, null, 8, ["value", "display-value", "mastered", "total"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(LingoCard, {
                  shadow: "none",
                  background: "gray",
                  rounded: "xl"
                }, {
                  title: _withCtx(() => [
                    _hoisted_14
                  ]),
                  action: _withCtx(() => [
                    _createVNode(_component_router_link, {
                      to: { name: 'lingo-student-levels-gaps', params: { studentId: studentId.value }, query: { activeTab: 'spelling' } }
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LingoButton, {
                          size: "sm",
                          hierarchy: "link-color",
                          class: "text-[#3065C5]"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Details ")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  default: _withCtx(() => [
                    _createVNode(LingoDataItem, { items: spellingDataItems.value }, null, 8, ["items"])
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("div", _hoisted_15, [
                _createVNode(LingoCard, {
                  shadow: "none",
                  background: "gray",
                  rounded: "xl"
                }, {
                  title: _withCtx(() => [
                    _hoisted_16
                  ]),
                  action: _withCtx(() => [
                    _createVNode(_component_router_link, {
                      to: `/main/lingo-vocabulary-list/student/${studentId.value}`
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LingoButton, {
                          size: "sm",
                          hierarchy: "link-color",
                          "data-testid": "btn-primary-sm"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Details ")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_17, [
                      _createVNode(LingoProgressCircle, {
                        size: "sm",
                        shape: 'half',
                        color: "#fbbf24",
                        caption: "Mastered",
                        "show-percent-sign": false,
                        value: vocabularyPercentage.value,
                        "display-value": vocabularyMasteryDisplay.value,
                        mastered: studentLingoData.value.currentVocabularyMasteredCount,
                        total: studentLingoData.value.currentVocabularyTotalCount
                      }, null, 8, ["value", "display-value", "mastered", "total"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(LingoCard, {
                  shadow: "none",
                  background: "gray",
                  rounded: "xl"
                }, {
                  title: _withCtx(() => [
                    _hoisted_18
                  ]),
                  action: _withCtx(() => [
                    _createVNode(_component_router_link, {
                      to: { name: 'lingo-student-levels-gaps', params: { studentId: studentId.value }, query: { activeTab: 'vocabulary' } }
                    }, {
                      default: _withCtx(() => [
                        _createVNode(LingoButton, {
                          size: "sm",
                          hierarchy: "link-color",
                          class: "text-[#3065C5]"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Details ")
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  default: _withCtx(() => [
                    _createVNode(LingoDataItem, { items: vocabularyDataItems.value }, null, 8, ["items"])
                  ]),
                  _: 1
                })
              ]),
              (grammarEnabled.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createVNode(LingoCard, {
                      shadow: "none",
                      background: "gray",
                      rounded: "xl"
                    }, {
                      title: _withCtx(() => [
                        _hoisted_20
                      ]),
                      action: _withCtx(() => [
                        _createVNode(_component_router_link, {
                          to: `/main/lingo-grammar-list/student/${studentId.value}`
                        }, {
                          default: _withCtx(() => [
                            _createVNode(LingoButton, {
                              size: "sm",
                              hierarchy: "link-color",
                              "data-testid": "btn-primary-sm"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Details ")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["to"])
                      ]),
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_21, [
                          _createVNode(LingoProgressCircle, {
                            size: "sm",
                            shape: "half",
                            color: "#fbbf24",
                            caption: "Mastered",
                            "show-percent-sign": false,
                            value: grammarPercentage.value,
                            "display-value": grammarMasteryDisplay.value,
                            mastered: studentLingoData.value.grammarMasteredCount,
                            total: studentLingoData.value.grammarTotalCount
                          }, null, 8, ["value", "display-value", "mastered", "total"])
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(LingoCard, {
                      shadow: "none",
                      background: "gray",
                      rounded: "xl"
                    }, {
                      title: _withCtx(() => [
                        _hoisted_22
                      ]),
                      action: _withCtx(() => [
                        _createVNode(_component_router_link, {
                          to: { name: 'lingo-student-levels-gaps', params: { studentId: studentId.value }, query: { activeTab: 'grammar' } }
                        }, {
                          default: _withCtx(() => [
                            _createVNode(LingoButton, {
                              size: "sm",
                              hierarchy: "link-color",
                              class: "text-[#3065C5]"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Details ")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["to"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(LingoDataItem, { items: grammarDataItems.value }, null, 8, ["items"])
                      ]),
                      _: 1
                    })
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _createVNode(LingoCard, {
                      shadow: "none",
                      background: "gray",
                      rounded: "xl"
                    }, {
                      default: _withCtx(() => [
                        _hoisted_24
                      ]),
                      _: 1
                    })
                  ])),
              (hasIncompleteDiagnostic.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_25, " *Early-stage level estimate. Full placement occurs after a small number of diagnostic lists. "))
                : _createCommentVNode("", true)
            ]))
    ])
  ], 64))
}
}

})